import type { ReactNode } from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader } from '../ui';
import { STATUS_CHIP_CLASS, STATUS_LABEL, type SectionMode, type SectionStatus } from './types';

export function SectionStatusChip({
  status,
  label,
}: {
  status: SectionStatus;
  label?: string;
}) {
  return (
    <span
      className={`text-caption font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_CHIP_CLASS[status]}`}
    >
      {label ?? STATUS_LABEL[status]}
    </span>
  );
}

interface SectionShellProps {
  mode: SectionMode;
  title: string;
  description?: string;
  status: SectionStatus;
  statusLabel?: string;
  testId?: string;
  error?: string | null;
  saving?: boolean;
  saved?: boolean;
  saveDisabled?: boolean;
  saveLabel?: string;
  onSave?: () => void;
  /** Footer propio (canales: la sección se guarda por partes). */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Marco común de toda sección: título + chip de estado + cuerpo + footer.
 * El footer por defecto cambia de texto según el contenedor; el resto no.
 */
export function SectionShell({
  mode,
  title,
  description,
  status,
  statusLabel,
  testId,
  error,
  saving = false,
  saved = false,
  saveDisabled = false,
  saveLabel,
  onSave,
  footer,
  children,
}: SectionShellProps) {
  const defaultSaveLabel = mode === 'wizard' ? 'Guardar y continuar →' : 'Guardar sección';

  return (
    <Card data-testid={testId} className="flex flex-col">
      <CardHeader className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-h6 text-neutral-900 dark:text-neutral-100">{title}</h2>
          {description && (
            <p className="text-body-sm text-neutral-500 dark:text-neutral-400 max-w-prose">
              {description}
            </p>
          )}
        </div>
        <SectionStatusChip status={status} label={statusLabel} />
      </CardHeader>

      <CardBody className="space-y-5">
        {error && (
          <div className="toast toast-error">
            <div className="toast-description">{error}</div>
          </div>
        )}
        {children}
      </CardBody>

      {(footer || onSave) && (
        <CardFooter className="flex items-center gap-3 flex-wrap pt-4">
          {footer ?? (
            <>
              {mode === 'edit' && saved && (
                <span className="text-body-sm font-medium text-success-700 dark:text-success-400">
                  ✓ Guardado
                </span>
              )}
              <span className="flex-1" />
              <Button
                variant="primary"
                onClick={onSave}
                disabled={saving || saveDisabled}
                loading={saving}
              >
                {saveLabel ?? defaultSaveLabel}
              </Button>
            </>
          )}
        </CardFooter>
      )}
    </Card>
  );
}
